import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { client, setActingCompanyForRequests, tokenStore } from '../api/client';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  /**
   * Which company an owner is currently acting as.
   *
   * A super admin belongs to no company, so the API cannot infer one and
   * refuses any company-scoped write without an explicit company_id. Rather
   * than a selector on every form, the choice is made once here and applied by
   * the request interceptor. Null for everyone else, who have exactly one
   * company and never see the control.
   */
  actingCompanyId: string | null;
  setActingCompany: (companyId: string | null) => void;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      actingCompanyId: null,

      setActingCompany(companyId) {
        setActingCompanyForRequests(companyId);
        set({ actingCompanyId: companyId });
      },

      async login(email, password) {
        const { data } = await client.post('/api/v1/auth/login', { email, password });
        tokenStore.set(data.tokens.accessToken, data.tokens.refreshToken);
        // Start every session clean of any acting company. logout() clears it,
        // but a direct re-login (account switch, expired session) never runs
        // logout, so without this a persisted selection from a previous
        // super-admin session leaks onto the new user's requests and the API
        // refuses them as cross-tenant. A super admin re-picks from the header.
        setActingCompanyForRequests(null);
        set({ user: data.user, isAuthenticated: true, actingCompanyId: null });
        return data.user as User;
      },

      async logout() {
        const refreshToken = tokenStore.refresh();
        if (refreshToken) {
          // Best effort: a failed revoke must not trap the user in a session
          // they have asked to leave.
          try {
            await client.post('/api/v1/auth/logout', { refreshToken });
          } catch {
            /* ignore */
          }
        }
        tokenStore.clear();
        // Cleared with the session: the next person to sign in on this
        // browser must not inherit a company somebody else was acting as.
        setActingCompanyForRequests(null);
        set({ user: null, isAuthenticated: false, actingCompanyId: null });
      },

      async refreshUser() {
        try {
          const { data } = await client.get('/api/v1/auth/me');
          set({ user: data, isAuthenticated: true });
        } catch {
          tokenStore.clear();
          set({ user: null, isAuthenticated: false });
        }
      },
    }),
    {
      name: 'ats-india-auth',
      // Tokens live in their own storage keys (see api/client.ts); persisting
      // them here as well would mean two sources of truth to invalidate.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        actingCompanyId: state.actingCompanyId,
      }),
      // The store rehydrates from localStorage on load, but the request
      // interceptor holds its own copy. Without this the selection survives a
      // refresh visually and stops being sent — the worst of both.
      onRehydrateStorage: () => (state) => {
        setActingCompanyForRequests(state?.actingCompanyId ?? null);
      },
    },
  ),
);

export const ROLE_LABELS: Record<User['role'], string> = {
  super_admin: 'Owner',
  company_admin: 'Managing Director',
  manager: 'Manager',
  recruiter: 'Recruiter',
  employee: 'Employee',
};

/** The owner alone. Distinct from isCompanyAdmin, which includes an MD.
 *
 * Defining a custom field adds a box to every candidate or employee form in a
 * company, which is closer to a schema change than to a setting — so it sits
 * with the owner rather than with whoever administers the company. */
export function isOwner(user: User | null): boolean {
  return user?.role === 'super_admin';
}

export function canSeeAllCompanies(user: User | null): boolean {
  return user?.role === 'super_admin';
}

/** Company admin and above.
 *
 * Distinct from isSupervisor, which includes managers. The /admin endpoints
 * require company_admin, so gating those screens on isSupervisor shows a
 * manager a page that can only fail. */
export function isCompanyAdmin(user: User | null): boolean {
  return user?.role === 'super_admin' || user?.role === 'company_admin';
}

/** Everyone who does recruitment work — which is everyone except an employee.
 *
 * The mirror of STAFF_ROLES on the server, which is what actually enforces
 * this. Hiding a tab an employee cannot use is courtesy; the 403 behind it is
 * the rule. Written as "not employee" rather than a list of the other four so
 * that a role added later has to be considered here rather than silently
 * inheriting recruitment access. */
export function isStaff(user: User | null): boolean {
  return user !== null && user.role !== 'employee';
}

export function isSupervisor(user: User | null): boolean {
  return (
    user?.role === 'super_admin'
    || user?.role === 'company_admin'
    || user?.role === 'manager'
  );
}
